import { cn, formatDateValue, stringToColor } from "@/lib/utils";
import { HistoryRecordType } from "@/types/dynamic-detail";
import { DateTime } from "luxon";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Avatar } from "../ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

// Type pour une ligne aplatie de changement
type FlatHistoryChange = {
    id: string;
    field_name: string;
    field_label: string;
    old_value: any;
    new_value: any;
    date: string;
    user: {
        name: string;
        monogram: string;
        url: string | null;
    };
    type: string;
};

// Type pour un groupe de changements
type ChangeGroup = {
    id: string;
    startDate: DateTime;
    endDate: DateTime;
    changes: FlatHistoryChange[];
};

// Type pour les changements regroupés par champ
type FieldGroup = {
    field_name: string;
    field_label: string;
    changes: FlatHistoryChange[];
    latestChange: FlatHistoryChange;
};

export function FieldsHistory({ history, className }: { history: HistoryRecordType[]; className?: string }) {
    const [selectedField, setSelectedField] = useState<string>("all");
    const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
    const groupingInterval = 30; // 30 secondes fixe

    // Aplatir l'historique pour avoir une ligne par changement
    const flatHistory: FlatHistoryChange[] = history.flatMap(record =>
        record.changes.map((change, changeIndex) => ({
            id: `${record.id}_${changeIndex}`,
            field_name: change.field,
            field_label: change.field_label,
            old_value: change.old_value,
            new_value: change.new_value,
            date: record.date,
            user: record.user,
            type: record.type
        }))
    );

    // Obtenir la liste unique des champs
    const availableFields = useMemo(() => {
        const fields = Array.from(new Set(flatHistory.map(change => change.field_label)));
        return fields.sort();
    }, [flatHistory]);

    // Filtrer l'historique selon le champ sélectionné
    const filteredHistory = useMemo(() => {
        if (selectedField === "all") {
            return flatHistory;
        }
        return flatHistory.filter(change => change.field_label === selectedField);
    }, [flatHistory, selectedField]);

    // Grouper les changements par intervalle de temps
    const groupedHistory = useMemo(() => {
        if (filteredHistory.length === 0) return [];

        // Trier par date (plus récent en premier)
        const sortedHistory = [...filteredHistory].sort((a, b) => DateTime.fromISO(b.date).toMillis() - DateTime.fromISO(a.date).toMillis());

        const groups: ChangeGroup[] = [];
        let currentGroup: FlatHistoryChange[] = [sortedHistory[0]];
        let groupStartDate = DateTime.fromISO(sortedHistory[0].date);

        for (let i = 1; i < sortedHistory.length; i++) {
            const currentDate = DateTime.fromISO(sortedHistory[i].date);
            const prevDate = DateTime.fromISO(sortedHistory[i - 1].date);

            // Si l'écart est > groupingInterval secondes, créer un nouveau groupe
            if (prevDate.diff(currentDate, "seconds").seconds > groupingInterval) {
                // Finaliser le groupe précédent
                const groupEndDate = DateTime.fromISO(currentGroup[currentGroup.length - 1].date);
                groups.push({
                    id: `group_${groups.length}`,
                    startDate: groupEndDate, // Le plus ancien du groupe
                    endDate: groupStartDate, // Le plus récent du groupe
                    changes: currentGroup
                });

                // Commencer un nouveau groupe
                currentGroup = [sortedHistory[i]];
                groupStartDate = currentDate;
            } else {
                currentGroup.push(sortedHistory[i]);
            }
        }

        // Ajouter le dernier groupe
        if (currentGroup.length > 0) {
            const groupEndDate = DateTime.fromISO(currentGroup[currentGroup.length - 1].date);
            groups.push({
                id: `group_${groups.length}`,
                startDate: groupEndDate,
                endDate: groupStartDate,
                changes: currentGroup
            });
        }

        return groups;
    }, [filteredHistory]);

    // Toggle l'expansion d'un champ
    const toggleFieldExpansion = (groupId: string, fieldName: string) => {
        const key = `${groupId}_${fieldName}`;
        setExpandedFields(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    // Regrouper les changements par champ dans un groupe
    const groupChangesByField = (changes: FlatHistoryChange[]): FieldGroup[] => {
        const fieldMap = new Map<string, FlatHistoryChange[]>();

        // Regrouper par field_name
        changes.forEach(change => {
            if (!fieldMap.has(change.field_name)) {
                fieldMap.set(change.field_name, []);
            }
            fieldMap.get(change.field_name)!.push(change);
        });

        // Créer les FieldGroup avec le changement le plus récent
        const fieldGroups: FieldGroup[] = [];
        fieldMap.forEach((fieldChanges, fieldName) => {
            // Trier par date (plus récent en premier)
            const sortedChanges = fieldChanges.sort((a, b) => DateTime.fromISO(b.date).toMillis() - DateTime.fromISO(a.date).toMillis());

            fieldGroups.push({
                field_name: fieldName,
                field_label: sortedChanges[0].field_label,
                changes: sortedChanges,
                latestChange: sortedChanges[0]
            });
        });

        return fieldGroups;
    };

    const renderTableRows = (changes: FlatHistoryChange[], groupId: string) => {
        if (changes.length === 0) {
            return (
                <tr className="transition-colors">
                    <td colSpan={5} className="p-4 py-6 text-center align-middle text-gray-500">
                        No changes found
                    </td>
                </tr>
            );
        }

        const fieldGroups = groupChangesByField(changes);
        const rows: React.ReactNode[] = [];

        fieldGroups.forEach(fieldGroup => {
            const expandKey = `${groupId}_${fieldGroup.field_name}`;
            const isExpanded = expandedFields.has(expandKey);
            const hasMultipleChanges = fieldGroup.changes.length > 1;

            // Toujours afficher la ligne de résumé
            const mostRecentChange = fieldGroup.latestChange; // Le plus récent (index 0)
            const oldestChange = fieldGroup.changes[fieldGroup.changes.length - 1]; // Le plus ancien (dernier index)

            // Valeurs pour le résumé
            const summaryOldValue = oldestChange.old_value; // La plus ancienne old_value
            const summaryNewValue = mostRecentChange.new_value; // La plus récente new_value
            const summaryDate = mostRecentChange.date; // Date du plus récent

            const summaryColor = mostRecentChange.user.name !== "System" ? stringToColor(mostRecentChange.user.name) : "#27272a";

            rows.push(
                <tr key={`summary_${fieldGroup.field_name}`} className="border-b transition-colors">
                    <td className="overflow-hidden px-2 py-1 pl-4 align-middle" style={{ width: "25%" }}>
                        <div className="truncate" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {fieldGroup.field_label}
                            {hasMultipleChanges && (
                                <button onClick={() => toggleFieldExpansion(groupId, fieldGroup.field_name)} className="hover:text-denim-blue-800 ml-2 cursor-pointer text-xs text-azure-blue">
                                    {isExpanded ? `(collapse)` : `(${fieldGroup.changes.length} changes)`}
                                </button>
                            )}
                        </div>
                    </td>
                    <td className="overflow-hidden px-2 py-1 align-middle" style={{ width: "20%" }}>
                        <div className="truncate" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            <span className="text-gray-500">{summaryOldValue || "—"}</span>
                        </div>
                    </td>
                    <td className="overflow-hidden px-2 py-1 align-middle" style={{ width: "20%" }}>
                        <div className="truncate" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            <span className="text-black">{summaryNewValue || "—"}</span>
                        </div>
                    </td>
                    <td className="overflow-hidden px-2 py-1 align-middle text-gray-500" style={{ width: "25%" }}>
                        <div className="truncate" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {formatDateValue(DateTime.fromISO(summaryDate), "timestamp")}
                        </div>
                    </td>
                    <td className="overflow-hidden px-2 py-1 text-center align-middle text-xs text-gray-400" style={{ width: "10%" }}>
                        <div className="truncate" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        {mostRecentChange.user.url ? (
                                            <Link to={mostRecentChange.user.url}>
                                                <Avatar
                                                    className={cn("flex h-6 w-6 cursor-pointer items-center justify-center text-[10px] font-medium text-white")}
                                                    style={{ backgroundColor: summaryColor }}
                                                >
                                                    {mostRecentChange.user.monogram}
                                                </Avatar>
                                            </Link>
                                        ) : (
                                            <Avatar className={cn("flex h-6 w-6 items-center justify-center text-[10px] font-medium text-white")} style={{ backgroundColor: summaryColor }}>
                                                {mostRecentChange.user.monogram}
                                            </Avatar>
                                        )}
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{mostRecentChange.user.name}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    </td>
                </tr>
            );

            // Si expanded, ajouter tous les changements détaillés
            if (isExpanded && hasMultipleChanges) {
                // Afficher tous les changements du groupe, triés par date décroissante (plus récent en haut)
                const allChanges = fieldGroup.changes; // Déjà triés par date décroissante
                allChanges.forEach(change => {
                    const changeColor = change.user.name !== "System" ? stringToColor(change.user.name) : "#27272a";

                    rows.push(
                        <tr key={change.id} className="border-y bg-blue-100 transition-colors">
                            <td className="overflow-hidden px-2 py-1 pl-4 align-middle" style={{ width: "25%" }}>
                                <div className="truncate" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {change.field_label}
                                </div>
                            </td>
                            <td className="overflow-hidden px-2 py-1 align-middle" style={{ width: "20%" }}>
                                <div className="truncate" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    <span className="text-gray-500">{change.old_value || "—"}</span>
                                </div>
                            </td>
                            <td className="overflow-hidden px-2 py-1 align-middle" style={{ width: "20%" }}>
                                <div className="truncate" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    <span className="text-black">{change.new_value || "—"}</span>
                                </div>
                            </td>
                            <td className="overflow-hidden px-2 py-1 align-middle text-gray-500" style={{ width: "25%" }}>
                                <div className="truncate" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {formatDateValue(DateTime.fromISO(change.date), "timestamp")}
                                </div>
                            </td>
                            <td className="overflow-hidden px-2 py-1 text-center align-middle text-xs text-gray-400" style={{ width: "10%" }}>
                                <div className="truncate" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                {change.user.url ? (
                                                    <Link to={change.user.url}>
                                                        <Avatar
                                                            className={cn("flex h-6 w-6 cursor-pointer items-center justify-center text-[10px] font-medium text-white")}
                                                            style={{ backgroundColor: changeColor }}
                                                        >
                                                            {change.user.monogram}
                                                        </Avatar>
                                                    </Link>
                                                ) : (
                                                    <Avatar className={cn("flex h-6 w-6 items-center justify-center text-[10px] font-medium text-white")} style={{ backgroundColor: changeColor }}>
                                                        {change.user.monogram}
                                                    </Avatar>
                                                )}
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>{change.user.name}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            </td>
                        </tr>
                    );
                });
            }
        });

        return <>{rows}</>;
    };

    return (
        <div className={cn("relative flex w-full flex-col", className)}>
            <div className="mt-4">
                {/* Filtre */}
                <div className="mb-4 flex items-center justify-end gap-2">
                    <Select value={selectedField} onValueChange={setSelectedField}>
                        <SelectTrigger className="w-52">
                            <SelectValue placeholder="Select a field" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All fields</SelectItem>
                            {availableFields.map(field => (
                                <SelectItem key={field} value={field}>
                                    {field}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="mb-2 text-xs text-gray-500">
                    Displaying <strong>{filteredHistory.length}</strong> out of <strong>{flatHistory.length}</strong> results
                    {groupedHistory.length > 1 && (
                        <span>
                            {" "}
                            • Grouped into <strong>{groupedHistory.length}</strong> time intervals (30s)
                        </span>
                    )}
                </div>

                {/* Table avec header unique et groupes */}
                {groupedHistory.length === 0 ? (
                    <div className="flex items-center justify-center p-8">
                        <div className="text-gray-500">No fields history available</div>
                    </div>
                ) : (
                    <div className="w-full">
                        <div className="overflow-hidden">
                            <table
                                className="w-full caption-bottom text-sm"
                                style={{
                                    tableLayout: "fixed",
                                    width: "100%"
                                }}
                            >
                                {/* Header unique */}
                                <thead className=" ">
                                    <tr className="">
                                        <th className="rounded-l-lg bg-gray-100 px-2 py-1.5 pl-4 text-left  font-normal" style={{ width: "25%" }}>
                                            <div className="overflow-hidden truncate text-ellipsis whitespace-nowrap pb-0">Field</div>
                                        </th>
                                        <th className=" bg-gray-100 px-2 py-1.5 text-left  font-normal" style={{ width: "20%" }}>
                                            <div className="overflow-hidden truncate text-ellipsis whitespace-nowrap pb-0">Old value</div>
                                        </th>
                                        <th className=" bg-gray-100 px-2 py-1.5 text-left  font-normal" style={{ width: "20%" }}>
                                            <div className="overflow-hidden truncate text-ellipsis whitespace-nowrap pb-0">New value</div>
                                        </th>
                                        <th className=" bg-gray-100 px-2 py-1.5 text-left  font-normal" style={{ width: "25%" }}>
                                            <div className="overflow-hidden truncate text-ellipsis whitespace-nowrap pb-0">Date</div>
                                        </th>
                                        <th className=" rounded-r-lg bg-gray-100 px-2 py-1.5 text-left  font-normal" style={{ width: "10%" }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupedHistory.map(group => (
                                        <React.Fragment key={group.id}>
                                            {/* Lignes des changements */}
                                            {renderTableRows(group.changes, group.id)}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
